import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Eye, FileText } from 'phosphor-react';
import { useSocietario } from '../../../../../hooks/useSocietario';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';

interface PreviewDeterministicoModalProps {
  /** ID do contrato persistido. Sem ele, botao desabilitado. */
  contratoId: number | null;
  /** Alteração: deriva o corpo (patches/vigente_hash/quadro_vigente_override) pro
   *  preview REAL do consolidado. Undefined em constituição/transformação (o back
   *  renderiza do próprio contrato, sem corpo). */
  montarBodyAlteracao?: () => Promise<Record<string, unknown> | undefined>;
}

/**
 * Sessao 2026-05-11 (item 26 Fase 5): preview deterministico do contrato
 * antes de "Gerar contrato". Renderiza esqueleto sem IA — placeholders
 * `[IA gerara: ...]` nos blocos de texto livre. Custo zero, ajuda
 * operador a pegar erros de estrutura.
 *
 * Botao "Preview" + modal com `<pre>` da resposta. Lazy load: so chama
 * endpoint quando abre.
 */
export const PreviewDeterministicoModal: React.FC<PreviewDeterministicoModalProps> = ({
  contratoId,
  montarBodyAlteracao,
}) => {
  const { previewDeterministico } = useSocietario();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [texto, setTexto] = useState<string | null>(null);
  // Transformação/v3 determinístico: o preview é o render REAL (sem placeholders
  // de IA). O backend sinaliza via `tem_placeholders=false`.
  const [temPlaceholders, setTemPlaceholders] = useState(true);
  // Motor novo: o que o operador precisa conferir no documento antes de gerar.
  const [conferir, setConferir] = useState<string[]>([]);

  const handleOpen = async () => {
    if (contratoId == null) {
      toast({
        status: 'warning',
        title: 'Salve o rascunho antes',
        description:
          'Preview só funciona com contrato persistido. Edite algum campo pra disparar o auto-save.',
      });
      return;
    }
    setOpen(true);
    setLoading(true);
    setTexto(null);
    setTemPlaceholders(true);
    setConferir([]);
    try {
      // Alteração: envia patches/vigente_hash/override pro preview REAL do
      // consolidado. Constituição/transformação: sem corpo (back renderiza do
      // contrato). Falha na derivação → undefined → back mostra "indisponível".
      const body = montarBodyAlteracao ? await montarBodyAlteracao() : undefined;
      const resp = await previewDeterministico(contratoId, body);
      setTexto(resp.texto);
      setTemPlaceholders(resp.tem_placeholders ?? true);
      setConferir(resp.conferir ?? []);
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Falha no preview',
        description: extractApiErrorMessage(err),
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        leftIcon={<Eye size={16} />}
        onClick={handleOpen}
        isDisabled={contratoId == null}
        title={
          contratoId == null
            ? 'Salve um rascunho primeiro (edite qualquer campo pra disparar auto-save)'
            : 'Renderiza esqueleto do contrato sem IA — útil pra verificar estrutura antes de gerar'
        }
      >
        Preview
      </Button>

      <Modal isOpen={open} onClose={() => setOpen(false)} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Box display="flex" alignItems="center" gap={2}>
              <FileText size={22} />
              <Text>Preview determinístico do contrato</Text>
            </Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
              <AlertIcon />
              <AlertDescription>
                {temPlaceholders ? (
                  <>
                    Esqueleto montado sem IA. Campos do form aparecem com valor real; cláusulas de
                    texto livre ficam como placeholders <strong>[IA gerará: …]</strong>. Útil pra
                    conferir estrutura e dados antes de gerar (que custa tokens IA).
                  </>
                ) : (
                  <>
                    Render <strong>determinístico completo</strong> — é exatamente o documento que
                    será gerado (sem IA, sem custo de tokens). Útil pra conferir o contrato final
                    antes de gerar.
                  </>
                )}
              </AlertDescription>
            </Alert>

            {loading && (
              <Box display="flex" justifyContent="center" py={10}>
                <Spinner size="lg" color="blue.500" />
              </Box>
            )}

            {!loading && conferir.length > 0 && (
              <Alert
                status="warning"
                borderRadius="md"
                mb={3}
                fontSize="sm"
                alignItems="flex-start"
              >
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold" mb={1}>
                    Conferir antes de gerar
                  </Text>
                  {conferir.map(item => (
                    <Text key={item}>• {item}</Text>
                  ))}
                </Box>
              </Alert>
            )}

            {!loading && texto && (
              <Box
                as="pre"
                bg="gray.50"
                p={3}
                borderRadius="md"
                borderWidth={1}
                borderColor="gray.200"
                fontSize="xs"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                maxH="60vh"
                overflowY="auto"
              >
                {texto}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setOpen(false)}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PreviewDeterministicoModal;
